import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import axios from 'axios';
import API_URL from '../../config';
import '../../App.css';

const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

function toDateInput(isoOrDate) {
  if (!isoOrDate) return '';
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

const AddDriverModal = ({ isOpen, onClose, onSuccess, driver = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    cdlNumber: '',
    cdlState: '',
    cdlExpiration: '',
    medicalCardExpiration: '',
    clearinghouseRegistered: false,
    clearinghouseLastQuery: '',
    drugTestCurrent: false,
    notes: '',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [limitError, setLimitError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setLimitError('');
    if (driver) {
      setFormData({
        name: driver.name || '',
        cdlNumber: driver.cdlNumber || '',
        cdlState: driver.cdlState || '',
        cdlExpiration: toDateInput(driver.cdlExpiration),
        medicalCardExpiration: toDateInput(driver.medicalCardExpiration),
        clearinghouseRegistered: !!driver.clearinghouseRegistered,
        clearinghouseLastQuery: toDateInput(driver.clearinghouseLastQuery),
        drugTestCurrent: !!driver.drugTestCurrent,
        notes: driver.notes || '',
      });
    } else {
      setFormData({
        name: '',
        cdlNumber: '',
        cdlState: '',
        cdlExpiration: '',
        medicalCardExpiration: '',
        clearinghouseRegistered: false,
        clearinghouseLastQuery: '',
        drugTestCurrent: false,
        notes: '',
      });
    }
  }, [isOpen, driver]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    name: formData.name.trim(),
    cdlNumber: formData.cdlNumber?.trim() || undefined,
    cdlState: formData.cdlState?.trim() || undefined,
    cdlExpiration: formData.cdlExpiration || undefined,
    medicalCardExpiration: formData.medicalCardExpiration || undefined,
    clearinghouseRegistered: formData.clearinghouseRegistered,
    clearinghouseLastQuery: formData.clearinghouseLastQuery || undefined,
    drugTestCurrent: formData.drugTestCurrent,
    notes: formData.notes?.trim() || undefined,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    setLimitError('');
    try {
      const token = localStorage.getItem('token');
      const payload = buildPayload();
      if (driver) {
        await axios.put(`${API_URL}/api/drivers/${driver.id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } else {
        await axios.post(`${API_URL}/api/drivers`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving driver:', err);
      const data = err.response?.data;
      if (data?.limitReached) {
        const msg =
          typeof data.message === 'string'
            ? data.message
            : 'Driver limit reached for your current plan. Upgrade to add more drivers.';
        setLimitError(msg);
        return;
      }
      const msg =
        data?.message ||
        (Array.isArray(data?.message) ? data.message.join(', ') : null) ||
        'Failed to save driver. Please try again.';
      alert(typeof msg === 'string' ? msg : 'Failed to save driver. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedDown: (eventData) => {
      if (eventData.first) {
        onClose();
      }
    },
    trackMouse: true,
    delta: 50,
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay fleet-edit-modal-overlay" onClick={onClose}>
      <div
        className="vehicle-modal fleet-edit-modal"
        onClick={(e) => e.stopPropagation()}
        {...swipeHandlers}
      >
        <div className="modal-header">
          <h2 className="modal-title">{driver ? 'Edit Driver' : 'Add a Driver to Your Team'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vehicle-form">
          <p
            style={{
              margin: '0 0 20px 0',
              fontSize: '0.875rem',
              lineHeight: 1.45,
              color: 'var(--muted)',
            }}
          >
            Track compliance for drivers you employ. Your own CDL and medical card are
            managed in your profile settings.
          </p>
          {limitError ? (
            <div className="drivers-limit-error" role="alert">
              <p>{limitError}</p>
              <Link to="/pricing">Upgrade your plan</Link>
            </div>
          ) : null}
          <div className="form-section">
            <h3 className="form-section-title">Driver information</h3>
            <div className="form-section-content">
              <div className="input-group">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Full name"
                  required
                />
                <label className="form-label">
                  Name <span className="required">*</span>
                </label>
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-row">
                <div className="input-group">
                  <input
                    type="text"
                    name="cdlNumber"
                    value={formData.cdlNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="CDL number"
                  />
                  <label className="form-label">CDL number</label>
                </div>
                <div className="input-group">
                  <select
                    name="cdlState"
                    value={formData.cdlState}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">State</option>
                    {usStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <label className="form-label">CDL state</label>
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <input
                    type="date"
                    name="cdlExpiration"
                    value={formData.cdlExpiration}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <label className="form-label">CDL expiration</label>
                </div>
                <div className="input-group">
                  <input
                    type="date"
                    name="medicalCardExpiration"
                    value={formData.medicalCardExpiration}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <label className="form-label">Medical card expiration</label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Compliance</h3>
            <div className="form-section-content">
              <div className="input-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="clearinghouseRegistered"
                  name="clearinghouseRegistered"
                  checked={formData.clearinghouseRegistered}
                  onChange={handleInputChange}
                />
                <label htmlFor="clearinghouseRegistered" className="form-label" style={{ marginBottom: 0 }}>
                  Clearinghouse registered
                </label>
              </div>
              <div className="input-group">
                <input
                  type="date"
                  name="clearinghouseLastQuery"
                  value={formData.clearinghouseLastQuery}
                  onChange={handleInputChange}
                  className="form-input"
                />
                <label className="form-label">Clearinghouse last query date</label>
              </div>
              <div className="input-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="drugTestCurrent"
                  name="drugTestCurrent"
                  checked={formData.drugTestCurrent}
                  onChange={handleInputChange}
                />
                <label htmlFor="drugTestCurrent" className="form-label" style={{ marginBottom: 0 }}>
                  Drug test current
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Additional information</h3>
            <div className="form-section-content">
              <div className="input-group">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-input textarea"
                  placeholder="Notes (optional)"
                  rows={4}
                />
                <label className="form-label">Notes</label>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="modal-button cancel" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="modal-button save" disabled={isSaving}>
              {isSaving ? 'Saving...' : driver ? 'Update Driver' : 'Add Driver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDriverModal;
